// Conditional Firebase support - works with or without Firebase installed
// For local development without Firebase, the app will use local OTP mode

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  return !!(
    process.env.NEXT_PUBLIC_FIREBASE_API_KEY &&
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN &&
    process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID
  )
}

// Type definitions
export type RecaptchaVerifier = any
export type FirebaseUser = any

// Initialize Firebase (only if configured and available)
let app: any = null
let auth: any = null
let firebaseInitialized = false
let firebaseModules: { firebaseApp: any; firebaseAuth: any } | null = null
let firebaseLoadPromise: Promise<{ firebaseApp: any; firebaseAuth: any } | null> | null = null
let hasWarnedMissingFirebase = false

// Lazy load Firebase modules
export async function loadFirebaseModules(): Promise<{ firebaseApp: any; firebaseAuth: any } | null> {
  if (firebaseModules || !isFirebaseConfigured() || typeof window === 'undefined') {
    return Promise.resolve(firebaseModules)
  }

  if (firebaseLoadPromise) {
    return firebaseLoadPromise
  }

  firebaseLoadPromise = (async () => {
    try {
      // @ts-ignore - Firebase may not be installed
      const firebaseApp = await import('firebase/app')
      // @ts-ignore - Firebase may not be installed
      const firebaseAuth = await import('firebase/auth')

      if (!firebaseApp.getApps().length) {
        const firebaseConfig = {
          apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
          authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
        }
        app = firebaseApp.initializeApp(firebaseConfig)
      } else {
        app = firebaseApp.getApps()[0]
      }
      auth = firebaseAuth.getAuth(app)
      firebaseInitialized = true
      firebaseModules = { firebaseApp, firebaseAuth }
      return firebaseModules
    } catch (error) {
      if (!hasWarnedMissingFirebase) {
        console.warn('Firebase not available, using local OTP mode:', error)
        hasWarnedMissingFirebase = true
      }
      firebaseModules = null
      return null
    } finally {
      firebaseLoadPromise = null
    }
  })()

  return firebaseLoadPromise
}

// Initialize on module load if configured
if (typeof window !== 'undefined' && isFirebaseConfigured()) {
  const modulesPromise = loadFirebaseModules()
  if (modulesPromise && typeof modulesPromise.catch === 'function') {
    modulesPromise.catch(() => {
      // Silently fail - will use local OTP mode
    })
  }
}

export { app, auth }

// Initialize Recaptcha Verifier
// Container must be provided by the calling component (e.g., PhoneOtpVerification)
export async function initializeRecaptcha(container: HTMLDivElement): Promise<RecaptchaVerifier> {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase not configured')
  }

  if (!container) {
    throw new Error('Recaptcha container is required')
  }

  const modules = await loadFirebaseModules()
  if (!modules || !modules.firebaseAuth || !auth) {
    throw new Error('Firebase Auth not initialized')
  }

  try {
    // Ensure container is ready
    container.innerHTML = ''
    container.style.display = 'none'
    container.style.visibility = 'hidden'

    return new modules.firebaseAuth.RecaptchaVerifier(auth, container, {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved
      },
      'expired-callback': () => {
        // Response expired. Ask user to solve reCAPTCHA again.
      }
    })
  } catch (error) {
    throw new Error('Firebase Auth not available')
  }
}

// Send OTP to phone number
export async function sendOTP(phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<{ confirmationResult: any }> {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase not configured')
  }

  const modules = await loadFirebaseModules()
  if (!modules || !modules.firebaseAuth || !auth) {
    throw new Error('Firebase Auth not initialized')
  }

  try {
    const confirmationResult = await modules.firebaseAuth.signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
    return { confirmationResult }
  } catch (error) {
    console.error('Error sending OTP:', error)
    throw error
  }
}

// Verify OTP
export async function verifyOTP(confirmationResult: any, code: string): Promise<FirebaseUser> {
  try {
    const result = await confirmationResult.confirm(code)
    return result.user
  } catch (error) {
    console.error('Error verifying OTP:', error)
    throw error
  }
}

// Get Firebase ID token
export async function getFirebaseIdToken(user: FirebaseUser): Promise<string> {
  return await user.getIdToken()
}

/**
 * Normalize phone number to E.164 format for Firebase.
 * Defaults to +91 when only a 10-digit number is provided.
 */
export function formatPhoneNumberForFirebase(phone: string, defaultCountryCode = '+91'): string {
  if (!phone) {
    throw new Error('Phone number is required')
  }

  const trimmed = phone.trim()
  if (trimmed.startsWith('+')) {
    return trimmed
  }

  const digitsOnly = trimmed.replace(/\D/g, '')
  if (!digitsOnly) {
    throw new Error('Invalid phone number format.')
  }

  const envCountryCode = process.env.NEXT_PUBLIC_DEFAULT_COUNTRY_CODE
  const normalizedDefaultCode = (envCountryCode || defaultCountryCode).startsWith('+')
    ? (envCountryCode || defaultCountryCode)
    : `+${envCountryCode || defaultCountryCode}`
  const countryDigits = normalizedDefaultCode.slice(1)

  if (digitsOnly.length === 10) {
    return `${normalizedDefaultCode}${digitsOnly}`
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith('0')) {
    return `${normalizedDefaultCode}${digitsOnly.slice(1)}`
  }

  if (digitsOnly.length === countryDigits.length + 10 && digitsOnly.startsWith(countryDigits)) {
    return `+${digitsOnly}`
  }

  if (digitsOnly.length > 10) {
    return `+${digitsOnly}`
  }

  throw new Error('Invalid phone number format.')
}
